import React, { useEffect } from "react";
import { BookingCardProps } from "./types";
import { CardWrapper } from "../PersonalDataCard/styles";
import { BookingDiv, Card, CardTitle } from "./styles";
import {
  DatePicker,
  LocalizationProvider,
  TimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Alert, Box, Button, CircularProgress, TextField } from "@mui/material";
import theme from "../../theme/theme";
import { postReservations } from "../../services/reservations/postReservations";
import { getResourceById } from "../../services/resources/getResources";
import { useParams } from "react-router-dom";
import { useToasts } from "react-toast-notifications";
import dayjs, { Dayjs } from "dayjs";
import utcPlugin from "dayjs/plugin/utc";
import timezonePlugin from "dayjs/plugin/timezone";
import { getTime } from "../../services/time/getTime";
import { getSystem } from "../../services/system/getSystem";
dayjs.extend(utcPlugin);
dayjs.extend(timezonePlugin);

export const BookingCard: React.FC<BookingCardProps> = ({ width, height }) => {
  const { id } = useParams();
  const resourceId = id || "No ID available";

  const [startDate, setStartDate] = React.useState<Dayjs | null>(null);
  const [endDate, setEndDate] = React.useState<Dayjs | null>(null);
  const [startTime, setStartTime] = React.useState<Dayjs | null>(null);
  const [endTime, setEndTime] = React.useState<Dayjs | null>(null);
  const [start, setStart] = React.useState<string | null>(null);
  const [end, setEnd] = React.useState<string | null>(null);
  const [serverResponse, setServerResponse] = React.useState(false); // Server response state
  const [description, setDescription] = React.useState(""); // Resource description state
  const [restrictHours, setRestrictHours] = React.useState<boolean>(false);
  const { addToast } = useToasts();
  const [loadingRequest, setLoadingRequest] = React.useState(false);
  const [currentDate, setCurrentDate] = React.useState<Dayjs | null>(null);
  const [systemIsActive, setSystemIsActive] = React.useState<boolean>(true);

  const timeZone = "America/Mexico_City";
  dayjs.tz.setDefault("America/Mexico_City");

  const convertTimeCurr2DB = (date: Dayjs | null, time: Dayjs | null): string => {
    if (!date || !time) return '';
  
    // Combina la fecha y la hora en un solo objeto Dayjs
    const combinedDateTime = date
      .hour(time.hour())
      .minute(time.minute())
      .second(time.second());
  
    // Convierte la fecha y hora combinadas a UTC
    // const utcCombinedDateTime = setUTCDate(combinedDateTime);
  
    // Formatea la fecha y hora en el formato deseado
    const formattedDate = combinedDateTime.format("YYYY-MM-DD HH:mm:ss.SSS");
    return formattedDate;
  };

  const setUTCDate = (date: dayjs.Dayjs): dayjs.Dayjs => {
    const utcDate = date.tz(timeZone).utc();
    return utcDate;
  };

  function convertDateToDayjs(utcDate: string): Dayjs {
    const mexicoCityTime = dayjs(utcDate)
      .utc() // Indica que la entrada es UTC
      .subtract(5, "hour") // Resta 6 horas para la zona horaria de Ciudad de México
      .tz("America/Mexico_City"); // Convierte a la zona horaria de Ciudad de México
    return mexicoCityTime;
  }

  // function convertTimeToDayjs(fechaString: string): Dayjs {
  //   // Crear un objeto Day.js a partir de la cadena de fecha y establecer la zona horaria
  //   const fecha = dayjs(fechaString).tz("America/Mexico_City");

  //   // Obtener solo la hora y los minutos en formato de 24 horas
  //   const horaMinutos = fecha.format("HH:mm");

  //   // Crear un nuevo objeto Day.js solo con la hora y los minutos
  //   const horaDayJS = dayjs(horaMinutos, { format: "HH:mm" });

  //   return horaDayJS;
  // }

  const NewReservation = () => {
    if (!startDate || !endDate || !startTime || !endTime || !description) {
      const missingFields = [];
      if (!startDate) missingFields.push("Fecha de inicio");
      if (!endDate) missingFields.push("Fecha de fin");
      if (!startTime) missingFields.push("Hora de inicio");
      if (!endTime) missingFields.push("Hora de fin");
      if (!description) missingFields.push("Descripción");

      const missingFieldsMessage = `Faltan llenar los siguientes campos: ${missingFields.join(
        ", "
      )}`;

      addToast(missingFieldsMessage, {
        appearance: "error",
        autoDismiss: true,
      });

      return;
    }

    if (startDate && endDate && startTime && endTime) {
      if (startDate.isAfter(endDate)) {
        addToast(
          `Error al hacer la reserva: la fecha ${endDate.format(
            "DD/MM/YYYY"
          )} de fin es anterior a la de inicio`,
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        return;
      }

      const minTime = currentDate?.hour(7).minute(0).second(0); // 7:00 AM
      const maxTime = currentDate?.hour(22).minute(0).second(0); // 10:00 PM

      if (
        startTime.hour() < 7 || startTime.hour() >= 22 ||
        endTime.hour() < 7 || endTime.hour() > 22
      ) {
        addToast(
          `Error al hacer la reserva: La hora ingresada debe estar entre las 7 AM y las 10 PM.`,
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        return;
      }

      if (
        startDate.isBefore(currentDate) &&
        !startDate.isSame(currentDate, "day")
      ) {
        addToast(
          `Error al hacer la reserva: la fecha ${startDate.format(
            "DD/MM/YYYY"
          )} de inicio no es válida`,
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        return;
      }

      const startDateTime = setUTCDate(startDate)
        .hour(startTime.hour())
        .minute(startTime.minute())
        .second(startTime.second());

      const endDateTime = setUTCDate(endDate)
        .hour(endTime.hour())
        .minute(endTime.minute())
        .second(endTime.second());

      if (endDateTime.isBefore(startDateTime)) {
        addToast(
          `Error al hacer la reserva: la hora de fin es anterior a la de inicio`,
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        return;
      }

      if (
        (startDate.isBefore(currentDate) &&
          !startDate.isSame(currentDate, "day")) ||
        (startDate.isSame(currentDate) &&
          startTime.isBefore(currentDate) &&
          !startTime.isSame(currentDate, "day"))
      ) {
        addToast(`Error al hacer la reserva: la hora de inicio no es válida`, {
          appearance: "error",
          autoDismiss: true,
        });
        return;
      }

      setLoadingRequest(true);
      addReservation();

      // Borra la información de los DatePickers y TimePickers después de hacer la reserva
      setStartDate(null);
      setEndDate(null);
      setStartTime(null);
      setEndTime(null);
      setDescription("");
    }
  };

  useEffect(() => {
    if (startDate && endDate && startTime && endTime) {
      const startDateTime = convertTimeCurr2DB(startDate, startTime);
      const endDateTime = convertTimeCurr2DB(endDate, endTime);
      setStart(startDateTime);
      setEnd(endDateTime);
    }
  }, [startDate, endDate, startTime, endTime]);

  useEffect(() => {
    if (startDate && endDate && startDate.isSame(endDate, "day")) {
      // Si la fecha de inicio y fin son el mismo día, establece la hora de inicio igual a la hora de fin
      setStartTime(endTime);
    } else {
      setStartTime(null);
      setEndTime(null);
    }
  }, [startDate, endDate]);

  // ------------------- JWT -------------------
  const jwt = localStorage.getItem("jwt");

  // ------------------- GET RESOURCES FROM API -------------------

  const addReservation = async () => {
    if (start && end) {
      await postReservations(
        jwt!,
        parseInt(resourceId),
        start,
        end,
        description
      )
        .then((res) => {
          if (res && res.data) {
            if (res.status !== 200) {
              setServerResponse(false);
              setLoadingRequest(false);
              addToast("Hubo un error al solicitar la reserva", {
                appearance: "error",
                autoDismiss: true,
              });
            } else {
              setServerResponse(false);
              setLoadingRequest(false);
              addToast("Solicitud de reserva exitosa", {
                appearance: "success",
                autoDismiss: true,
              });
            }
          }
        })
        .catch((err) => {
          setServerResponse(true);
          setLoadingRequest(false);
          addToast("Hubo un error al solicitar la reserva", {
            appearance: "error",
            autoDismiss: true,
          });
        });
    }
  };

  const getSystemStatus = async () => {
    await getSystem(jwt!)
    .then((res: any) => {
      if (res && res.data) {
        setSystemIsActive(res.data.is_active);
      }
    })
    .catch((err: any) => {
      console.log("Error: ", err);
    });
  }

  const getCurrentTime = async () => {
    await getTime(jwt!)
      .then((res: any) => {
        if (res && res.data) {
          // setCurrentTime(convertTimeToDayjs(res.data.server_time));
          setCurrentDate(convertDateToDayjs(res.data.server_time));
        }
      })
      .catch((err: any) => {
        console.log("Error: ", err);
      });
  };

  const handleTimePickerChange = (
    newValue: Dayjs | null,
    isStartTime: boolean
  ) => {
    if (newValue) {
      if (isStartTime) {
        setStartTime(newValue);
      } else {
        setEndTime(newValue);
      }
    }
  };


  useEffect(() => {
    getCurrentTime();
    getSystemStatus();
  }, []);

  // const getCurrentHour = () => currentTime?.hour();

  return (
    <>
      <Card>
        <CardWrapper>
          <CardTitle>Reserva</CardTitle>
          <BookingDiv>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Fecha de inicio"
                value={startDate}
                minDate={currentDate ? currentDate : undefined} // Disable dates before the current date
                onChange={(newValue: Dayjs | null) => setStartDate(newValue)}
                slotProps={{
                  textField: {
                    required: true,
                    sx: { marginRight: "-1px" },
                    InputProps: {
                      style: {
                        borderRadius: "10px 0 0 10px", // Agregar el borderRadius aquí
                      },
                    },
                  },
                }}
              />
              <DatePicker
                label="Fecha de fin"
                value={endDate}
                minDate={currentDate ? currentDate : undefined} // Disable dates before the current date
                onChange={(newValue: Dayjs | null) => setEndDate(newValue)}
                slotProps={{
                  textField: {
                    required: true,
                    InputProps: {
                      style: {
                        borderRadius: "0 10px 10px 0", // Agregar el borderRadius aquí
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </BookingDiv>
          <BookingDiv>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <TimePicker
                label="Hora de inicio"
                value={startTime}
                minTime={
                  currentDate?.isSame(startDate, "day")
                    ? currentDate.isBefore(startDate?.hour(7).minute(0))
                      ? startDate?.hour(7).minute(0)
                      : currentDate.add(1, "minute")
                    : startDate?.hour(7).minute(0)
                }
                maxTime={currentDate?.hour(22).minute(0)}
                onChange={(newValue: Dayjs | null) => {
                  handleTimePickerChange(newValue, true);
                }}
                slotProps={{
                  textField: {
                    required: true,
                    sx: { marginRight: "-1px" },
                    InputProps: {
                      style: {
                        borderRadius: "10px 0 0 10px",
                        width: "100%",
                      },
                    },
                  },
                }}
                // Deshabilita AM si restrictHours es true
                ampm={false}
              />
              <TimePicker
                label="Hora de fin"
                value={endTime}
                minTime={
                  currentDate?.isBefore(currentDate.hour(7).minute(0)) // Comprueba si la hora actual es antes de las 7 AM
                    ? currentDate?.hour(7).minute(0) // Establece 7 AM como hora mínima
                    : startDate && startDate.isSame(currentDate, "day")
                    ? currentDate?.add(1, "minute")
                    : currentDate?.hour(7).minute(0)
                }
                maxTime={currentDate?.hour(22).minute(0)}
                onChange={(newValue: Dayjs | null) => {
                  handleTimePickerChange(newValue, false);
                }}
                slotProps={{
                  textField: {
                    required: true,
                    InputProps: {
                      style: {
                        borderRadius: "0 10px 10px 0",
                      },
                    },
                  },
                }}
                ampm={false}
              />
            </LocalizationProvider>
          </BookingDiv>
          <BookingDiv>
            <TextField
              label="Descripción"
              value={description}
              required
              multiline
              rows={2}
              defaultValue=""
              variant="outlined"
              fullWidth
              onChange={(e) => setDescription(e.target.value)}
              InputProps={{
                style: {
                  borderRadius: "10px",
                },
              }}
            />
          </BookingDiv>
          <Box sx={{ m: 1, position: "relative" }}>
            <Button
              variant="contained"
              sx={{
                width: "100%",
                marginTop: "0.8rem",
                borderRadius: "15px",
                fontSize: "16px",
                backgroundColor: theme.color.navy,
                color: theme.color.white,
                textTransform: "none",
                "&:hover": {
                  backgroundColor: theme.color.space,
                },
              }}
              onClick={NewReservation}
              disabled={loadingRequest || !systemIsActive}
            >
              Solicitar reserva
            </Button>
            {loadingRequest && (
              <CircularProgress
                size={24}
                sx={{
                  color: theme.color.navy,
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  marginTop: "-6px",
                  marginLeft: "-12px",
                }}
              />
            )}
          </Box>
          { systemIsActive ? (
            <Alert
              severity="info"
              sx={{
                borderRadius: "10px",
                margin: "1.5rem 0 -0.5rem 0",
                fontSize: "11px",
              }}
            >
              Los horarios de reservación se muestran en la zona horaria del Hub
              de Ciberseguridad <strong>(Ciudad de México).</strong>
            </Alert>
            ) : (
            <Alert
              severity="error"
              sx={{
                borderRadius: "10px",
                margin: "1.5rem 0 -0.5rem 0",
                fontSize: "12px",
              }}
            >
              Por el momento, no se pueden realizar reservaciones. El Hub de Ciberseguridad <strong>está en receso</strong>.
            </Alert>
            )}
        </CardWrapper>
      </Card>
    </>
  );
};

export default BookingCard;
